import React, { FC, useContext, useEffect, useState } from 'react';

import {
  H3,
  ITabOption,
  Separator,
  Tabs,
  Toggle,
  Tooltip,
} from '@smartshell/ui-kit';
import cn from 'classnames';

import { ColorPicker, SettingsFrame, SettingsLock } from 'components';
import { useIsFeatureAvailable } from 'features';
import { LocalizationContext } from 'localization';
import { SettingsContext } from 'settings';

import { ClientImage } from './ClientImage';
import { IMAGES_LINKS } from './constants';
import { IShellBackgroundProps } from './interfaces';
import s from './ShellBackground.module.scss';
import { getOptimizedImage, isDefaultImage } from './utils';

export const ShellBackground: FC<IShellBackgroundProps> = ({
  bgColor,
  bgImage,
  setState,
}) => {
  const { t } = useContext(LocalizationContext);
  const {
    state: { settings },
  } = useContext(SettingsContext);

  const OPTIONS: Array<ITabOption<'image' | 'color'>> = [
    {
      title: t('settings.shell.customization.background.tab.cover'),
      value: 'image',
    },
    {
      title: t('settings.shell.customization.background.tab.onlyCover'),
      value: 'color',
    },
  ];

  const isAvailable = useIsFeatureAvailable('shell_skin');
  const [customImage, setCustomImage] = useState<string | null>(null);
  const [tab, setTab] = useState<'image' | 'color'>(
    bgColor && !bgImage ? 'color' : 'image'
  );

  const setSelectImageHandler = (imageLink: string): void => {
    setState((prev) => ({ ...prev, shellBgSkin: imageLink }));

    if (isDefaultImage(imageLink)) {
      setCustomImage(null);
    } else {
      setCustomImage(imageLink);
    }
  };

  useEffect(() => {
    const initialImage = bgImage || IMAGES_LINKS[0];

    if (!bgImage) {
      setTab('color');
      setColorHandler(bgColor || '#232323');
      return;
    } else {
      setTab('image');
    }

    setState((prev) => ({ ...prev, shellBgSkin: initialImage }));

    if (isDefaultImage(initialImage)) {
      setCustomImage(null);
    } else {
      setCustomImage(initialImage);
    }
  }, [bgImage, setState]);

  const changeTabHandler = (value: 'image' | 'color'): void => {
    setTab(value);

    if (value === 'image') {
      setState((prev) => ({
        ...prev,
        shellBgSkin: settings.shellBgSkin || IMAGES_LINKS[0],
        shellSkinBackgroundColor: settings.shellSkinBackgroundColor || '',
      }));
    } else {
      setState((prev) => ({
        ...prev,
        shellBgSkin: null,
        shellSkinBackgroundColor:
          settings.shellSkinBackgroundColor || '#232323',
      }));
    }
  };

  const setColorHandler = (color: string): void => {
    setState((prev) => ({ ...prev, shellSkinBackgroundColor: color }));
  };

  return (
    <SettingsFrame>
      <div className={s.container}>
        <div className={s.header}>
          <H3>{t('settings.shell.customization.background.title')}</H3>
          {isAvailable ? (
            <Tooltip
              contentClassName={s.tooltip_content}
              position="bottom-left"
              content={
                <div className={s.tooltip_frame}>
                  <H3>{t('setting.shell.bg.title')}</H3>
                  <ul className={s.tooltip_list}>
                    <li className={s.tooltip_item}>
                      <span className={s.tooltip_text}>
                        {t('setting.shell.bg.optimal')}
                      </span>
                      <span className={s.tooltip_value}>1920х1080</span>
                    </li>
                    <li className={s.tooltip_item}>
                      <span className={s.tooltip_text}>
                        {t('application.image.maxSize')}
                      </span>
                      <span className={s.tooltip_value}>
                        2 {t('setting.shell.bg.optimal.size')}
                      </span>
                    </li>
                    <li className={s.tooltip_item}>
                      <span className={s.tooltip_text}>
                        {t('application.image.format')}
                      </span>
                      <span className={s.tooltip_value}>.jpg, .png</span>
                    </li>
                  </ul>
                </div>
              }
            />
          ) : (
            <SettingsLock />
          )}
        </div>
        <span className={s.subtitle}>
          {t('settings.shell.customization.background.description')}
        </span>
        <Tabs
          options={OPTIONS}
          onChange={changeTabHandler}
          value={tab}
          className={s.tab}
        />
        {tab === 'image' ? (
          <>
            <div className={cn(s.images, !isAvailable && s.disabled)}>
              {IMAGES_LINKS.map((imageLink) => (
                <div
                  className={cn(
                    s.imageContainer,
                    bgImage === imageLink && s.selected,
                    !isAvailable && s.disabled
                  )}
                  key={imageLink}
                  onClick={() => {
                    if (!isAvailable) return;

                    setSelectImageHandler(imageLink);
                  }}
                >
                  <div className={s.circle} />
                  <img
                    src={getOptimizedImage(imageLink)}
                    alt={t('setting.shell.bg.alt')}
                    className={s.image}
                  />
                </div>
              ))}
              <ClientImage
                onUpload={setSelectImageHandler}
                imageLink={customImage}
                disabled={!isAvailable}
              />
            </div>
            <Separator className={s.separator} />
            <div className={s.color}>
              <Toggle
                onChange={(value: string) =>
                  setColorHandler(
                    value ? settings.shellSkinBackgroundColor || '#232323' : ''
                  )
                }
                value={!!bgColor}
                label={t('settings.shell.customization.backgroundColor.title')}
                description={t(
                  'settings.shell.customization.backgroundColor.description'
                )}
                variant="secondary"
                emptyBg
                disabled={!isAvailable}
              />

              <ColorPicker
                color={bgColor || '#232323'}
                setColor={setColorHandler}
                disabled={!bgColor || !isAvailable}
              />
            </div>
          </>
        ) : (
          <ColorPicker
            color={bgColor}
            setColor={setColorHandler}
            disabled={!isAvailable}
          />
        )}
      </div>
    </SettingsFrame>
  );
};
